import { AccountApi } from 'services/account';
import { AuthApi } from 'services/auth';
import {
  getAuthSessionRevision,
  isAuthSessionCurrent,
  replaceAuthTokens,
} from 'services/auth/session';
import type { IUser } from 'types';

const RESYNC_INTERVAL_MS = 30_000;
const JWT_PAYLOAD_PART = 1;

let lastResyncAt = 0;

// The access token carries the role in its `auth` claim, fixed when the token is
// issued. After an admin approves someone or changes a role, the token held by that
// person is stale until it is reissued, and role-protected endpoints answer 403.
const getAccessTokenRoles = (accessToken: string | null): string[] | null => {
  try {
    const payload = accessToken?.split('.')[JWT_PAYLOAD_PART];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof claims.auth === 'string'
      ? claims.auth
          .split(',')
          .map((role: string) => role.trim())
          .filter(Boolean)
      : null;
  } catch {
    return null;
  }
};

const isAccessTokenStale = (account: Pick<IUser, 'role'>): boolean => {
  const roles = getAccessTokenRoles(localStorage.getItem('accessToken'));
  return roles !== null && !roles.includes(account.role.name);
};

/**
 * Reissues the tokens from the server's current account data when they predate a role
 * change. Resolves true only if the new token matches the account, so callers can
 * safely reload without risking a loop.
 */
const syncTokensWithAccount = async (account: Pick<IUser, 'role'>): Promise<boolean> => {
  if (!isAccessTokenStale(account)) return false;

  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  if (!accessToken || !refreshToken) return false;
  const owner = getAuthSessionRevision();

  const tokens = await new AuthApi().refreshAccountAccessToken({
    accessToken,
    refreshToken,
  });
  // A late response belongs to the session that started it. Never overwrite a
  // logout, another login, or tokens already replaced by a newer refresh.
  if (!replaceAuthTokens(owner, { accessToken, refreshToken }, tokens)) {
    return false;
  }

  return !isAccessTokenStale(account);
};

const canAttemptSessionResync = (): boolean =>
  Boolean(localStorage.getItem('accessToken')) &&
  Date.now() - lastResyncAt > RESYNC_INTERVAL_MS;

/** Re-checks a signed-in session after a 403: true if stale tokens were repaired. */
const resyncStaleSession = async (): Promise<boolean> => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  if (!accessToken || !refreshToken) return false;
  const owner = getAuthSessionRevision();

  lastResyncAt = Date.now();
  const account = await new AccountApi().me({ suppressForbiddenEvent: true });
  // Old account details must not be used to refresh a different session.
  if (!isAuthSessionCurrent(owner)) {
    return false;
  }
  return syncTokensWithAccount(account);
};

export {
  canAttemptSessionResync,
  getAccessTokenRoles,
  resyncStaleSession,
  syncTokensWithAccount,
};
